import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ChevronLeft, ChevronRight, Heart } from "lucide-react";

/**
 * PartnerAppreciation
 * A respectful, editorial partner-message carousel.
 * Same appreciation message is spoken by each partner organization.
 */
const APPRECIATION_MESSAGE =
  "Thank you for your service and for the sacrifices you and your family have made on behalf of our nation. As a WHO partner, we are honored to stand with you and help ensure you are connected to the support, opportunities, and resources you deserve.";

const DEFAULT_PARTNERS = [
  {
    initials: "VSO",
    name: "Veteran Service Organizations",
    category: "Advocacy · Benefits · Peer Support",
  },
  {
    initials: "HC",
    name: "Regional Healthcare Systems",
    category: "Healthcare · Behavioral Health",
  },
  {
    initials: "WD",
    name: "Workforce Development Partners",
    category: "Career Transition · Skills",
  },
  {
    initials: "HED",
    name: "Colleges & Universities",
    category: "Higher Education · Research",
  },
  {
    initials: "HSG",
    name: "Housing & Community Providers",
    category: "Housing · Stability",
  },
  {
    initials: "PHL",
    name: "Philanthropic Funders",
    category: "Philanthropy · Impact",
  },
];

export default function PartnerAppreciation({
  partners = DEFAULT_PARTNERS,
  showCta = true,
  autoRotate = true,
  intervalMs = 7000,
  eyebrow = "Partner appreciation",
  title = "A word from those who stand with our veterans.",
}) {
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);

  const go = useCallback(
    (delta) => {
      setIdx((cur) => (cur + delta + partners.length) % partners.length);
    },
    [partners.length]
  );

  useEffect(() => {
    if (!autoRotate || paused) return;
    const id = setInterval(() => go(1), intervalMs);
    return () => clearInterval(id);
  }, [autoRotate, paused, intervalMs, go]);

  const current = partners[idx];

  return (
    <section
      className="relative py-24 lg:py-28 bg-navy text-cream border-t border-navy-800 overflow-hidden"
      data-testid="partner-appreciation-section"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {/* atmosphere */}
      <div className="absolute inset-0 pointer-events-none" aria-hidden>
        <div className="absolute -top-40 -left-40 w-[520px] h-[520px] rounded-full bg-gold/8 blur-3xl" />
        <div className="absolute -bottom-40 -right-40 w-[520px] h-[520px] rounded-full bg-cream/[0.04] blur-3xl" />
      </div>

      <div className="relative max-w-[1100px] mx-auto px-6 lg:px-10">
        {/* header */}
        <div className="max-w-[720px]">
          <p className="eyebrow text-gold flex items-center gap-2" data-testid="partner-appreciation-eyebrow">
            <Heart className="w-3.5 h-3.5" strokeWidth={2.5} /> {eyebrow}
          </p>
          <h2 className="mt-4 font-display font-black text-cream tracking-tight leading-[1.05] text-3xl sm:text-4xl lg:text-[2.6rem]">
            {title}
          </h2>
          <div className="mt-5 h-[3px] w-14 bg-gold rounded-full" aria-hidden />
        </div>

        {/* carousel body */}
        <div className="mt-12 grid lg:grid-cols-12 gap-10 items-center">
          {/* Partner mark */}
          <div className="lg:col-span-4 flex justify-center lg:justify-start">
            <AnimatePresence mode="wait">
              <motion.div
                key={`mark-${idx}`}
                initial={{ opacity: 0, y: 12, scale: 0.98 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -12, scale: 0.98 }}
                transition={{ duration: 0.5 }}
                className="text-center"
                data-testid="partner-mark"
              >
                <div className="relative mx-auto w-40 h-40 lg:w-48 lg:h-48 rounded-2xl bg-white/[0.04] border border-gold/40 flex items-center justify-center shadow-2xl">
                  <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-gold/12 to-transparent" aria-hidden />
                  <span
                    className="relative font-display font-black text-gold text-4xl lg:text-5xl tracking-tight"
                    data-testid="partner-mark-initials"
                  >
                    {current.initials}
                  </span>
                </div>
                <p className="mt-5 text-cream font-display font-bold text-lg" data-testid="partner-mark-name">
                  {current.name}
                </p>
                <p className="mt-1 text-xs uppercase tracking-[0.22em] text-cream/55 font-semibold">
                  {current.category}
                </p>
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Appreciation message */}
          <div className="lg:col-span-8">
            <blockquote
              className="relative pl-6 lg:pl-8 border-l-4 border-gold"
              data-testid="partner-appreciation-message"
            >
              <span
                className="font-display font-black text-gold text-6xl lg:text-7xl leading-none select-none absolute -top-6 -left-2 opacity-40"
                aria-hidden
              >
                &ldquo;
              </span>
              <AnimatePresence mode="wait">
                <motion.p
                  key={`msg-${idx}`}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.5 }}
                  className="font-display font-medium text-cream text-xl lg:text-[1.55rem] leading-[1.4]"
                >
                  {APPRECIATION_MESSAGE}
                </motion.p>
              </AnimatePresence>
              <footer className="mt-6 text-xs uppercase tracking-[0.22em] text-cream/55 font-semibold">
                &mdash; {current.name}, a WHO partner
              </footer>
            </blockquote>

            {showCta && (
              <div className="mt-8 flex flex-wrap items-center gap-4">
                <a
                  href="/partners#partner-resources"
                  data-testid="partner-appreciation-cta"
                  className="inline-flex items-center gap-2 bg-gold hover:bg-gold-deep text-navy font-semibold px-5 py-3 rounded-md transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold focus-visible:ring-offset-2 focus-visible:ring-offset-navy"
                >
                  Explore Partner Resources <ArrowRight className="w-4 h-4" strokeWidth={2.5} />
                </a>
                <p className="text-sm text-cream/55">
                  Verified services · offered through the WHO platform
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Controls + progress dots */}
        <div className="mt-12 flex items-center justify-between gap-4">
          <div className="flex items-center gap-2" data-testid="partner-appreciation-dots">
            {partners.map((p, i) => (
              <button
                key={p.name}
                onClick={() => setIdx(i)}
                aria-label={`Show ${p.name}`}
                data-testid={`partner-dot-${i}`}
                className={`h-1.5 rounded-full transition-all ${
                  i === idx ? "w-8 bg-gold" : "w-3 bg-white/25 hover:bg-white/50"
                }`}
              />
            ))}
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => go(-1)}
              data-testid="partner-appreciation-prev"
              aria-label="Previous partner"
              className="w-10 h-10 rounded-full border border-white/20 hover:border-gold hover:text-gold text-cream flex items-center justify-center transition-colors"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button
              onClick={() => go(1)}
              data-testid="partner-appreciation-next"
              aria-label="Next partner"
              className="w-10 h-10 rounded-full border border-white/20 hover:border-gold hover:text-gold text-cream flex items-center justify-center transition-colors"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
